// src/components/mentions/MentionTextarea.tsx
import React, { useState, useRef, ChangeEvent } from 'react'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { useTheme } from '@/contexts/ThemeContext'
import type { Member } from '@/types/user'

interface MentionTextareaProps {
  value: string
  onChange: (newValue: string, mentioned: Member[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export default function MentionTextarea({
  value,
  onChange,
  placeholder = '',
  className = '',
  disabled = false,
}: MentionTextareaProps) {
  const { isDarkMode } = useTheme()
  const textareaRef   = useRef<HTMLTextAreaElement>(null)
  const mentionedUsers = useRef<Set<Member>>(new Set())
  const [showMentions, setShowMentions] = useState(false)
  const [members,      setMembers]      = useState<Member[]>([])

  async function fetchMembers(search: string) {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    const { data } = await supabase
      .from('users')
      .select('id, auth_id, full_name, avatar_url, city')
      .neq('auth_id', user!.id)
      .ilike('full_name', `%${search}%`)
      .limit(10)
    setMembers(data ?? [])
  }

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const newValue = e.target.value
const cleanMentioned = Array.from(mentionedUsers.current).filter(m =>
  newValue.includes(`@${m.full_name}`)
);
mentionedUsers.current = new Set(cleanMentioned);
onChange(newValue, cleanMentioned);


    const cursorPos  = e.target.selectionStart
    const textBefore = newValue.slice(0, cursorPos)
    const atIndex    = textBefore.lastIndexOf('@')

    if (
      atIndex !== -1 &&
      (atIndex === 0 || /\s/.test(textBefore[atIndex - 1]))
    ) {
      const query = textBefore.slice(atIndex + 1)
      if (/^[\w ]*$/.test(query)) {
        fetchMembers(query)
        setShowMentions(true)
        return
      }
    }

    setShowMentions(false)
  }

  function insertMention(member: Member) {
  const ta = textareaRef.current!;
  const cursorPos = ta.selectionStart;
  const before = value.slice(0, cursorPos);
  const atIndex = before.lastIndexOf('@');
  const prefix = value.slice(0, atIndex);
  const suffix = value.slice(cursorPos);
  const mentionText = `@${member.full_name} `;

  const newValue = prefix + mentionText + suffix;

  // 🛡️ Prevent duplicate mentions
  let alreadyMentioned = false;
  mentionedUsers.current.forEach(existing => {
    if (existing.auth_id === member.auth_id) alreadyMentioned = true;
  });
  if (!alreadyMentioned) mentionedUsers.current.add(member);

  onChange(newValue, Array.from(mentionedUsers.current));
  setShowMentions(false);

  // put cursor right after inserted mention
  setTimeout(() => {
    const pos = prefix.length + mentionText.length;
    ta.setSelectionRange(pos, pos);
    ta.focus();
  }, 0);
}


  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        className={cn(
          className,
          'w-full p-4 focus:outline-none focus:placeholder-transparent',
          isDarkMode ? 'bg-dark-300 text-white caret-white' : 'bg-white text-black caret-black'
        )}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
      />

      {showMentions && members.length > 0 && (
        members.length <= 2 ? (
          // for 1–2 items, show just above the textarea
          <ul
            className={cn(
              'absolute z-50 inset-x-4 mb-2 rounded-lg shadow-lg overflow-auto',
              isDarkMode
                ? 'bg-dark-200 border border-dark-300'
                : 'bg-white border border-gray-200'
            )}
            style={{ bottom: '100%' }} // sit immediately above textarea
          >
            {members.map(m => (
              <li
                key={m.id}
                className={cn(
                  'flex items-center p-2 cursor-pointer',
                  isDarkMode ? 'hover:bg-dark-300' : 'hover:bg-gray-100'
                )}
                onClick={() => insertMention(m)}
              >
                <img
                  src={m.avatar_url}
                  alt={m.full_name}
                  className="w-6 h-6 rounded-full mr-2"
                />
                <div>
                  <div className={cn(
                    'font-medium',
                    isDarkMode ? 'text-white' : 'text-black'
                  )}>
                    {m.full_name}
                  </div>
                  <div className={cn(
                    'text-xs',
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  )}>
                    {m.city}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          // for 3+ items, pin above the keyboard
          <ul
            className={cn(
              'fixed z-50 inset-x-4 bottom-4 max-h-60 overflow-auto rounded-lg shadow-lg',
              isDarkMode
                ? 'bg-dark-200 border border-dark-300'
                : 'bg-white border border-gray-200'
            )}
          >
            {members.map(m => (
              <li
                key={m.id}
                className={cn(
                  'flex items-center p-2 cursor-pointer',
                  isDarkMode ? 'hover:bg-dark-300' : 'hover:bg-gray-100'
                )}
                onClick={() => insertMention(m)}
              >
                <img
                  src={m.avatar_url}
                  alt={m.full_name}
                  className="w-6 h-6 rounded-full mr-2"
                />
                <div>
                  <div className={cn(
                    'font-medium',
                    isDarkMode ? 'text-white' : 'text-black'
                  )}>
                    {m.full_name}
                  </div>
                  <div className={cn(
                    'text-xs',
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  )}>
                    {m.city}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  )
}
